import { useRef } from 'react';

import useMediaAnalytics from '@/utils/hooks/swr/useMediaAnalytics';

import { useDatepickerGlobalStore } from '@/utils/zustand/providers/datepicker-global-provider';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

import PropTypes from 'prop-types';

import { postFooterStats } from '@/lib/posts/postFooterStats';

import Post from '@/components/base/Post';

/**
 * Component that lazy loads analytics for a media card using Intersection Observer
 * Only fetches analytics when the card becomes visible
 */
const LazyAnalyticsFooter = ({ type, feedType = '', mediaId, socialProfileId, network }) => {
    const containerRef = useRef(null);
    const isVisible = useIntersectionObserver(containerRef, {
        threshold: 0.5,
        rootMargin: '50px',
        freezeOnceVisible: true
    });
    const globalDate = useDatepickerGlobalStore((state) => state.date);

    // Get required fields for this network and type
    // Falls back to empty array if network or type config doesn't exist
    const requiredFields = postFooterStats[network]?.[type]?.default ?? [];

    const { analytics, isLoading, analyticsError } = useMediaAnalytics({
        mediaId,
        socialProfileId,
        network,
        requiredFields,
        startDate: globalDate?.from,
        endDate: globalDate?.to,
        shouldFetch: isVisible && !!mediaId && !!socialProfileId && requiredFields.length > 0,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    return (
        <div ref={containerRef}>
            <Post.Footer
                type={type}
                feedType={feedType}
                analytics={analytics}
                isLoadingAnalytics={isLoading && !analyticsError}
            />
        </div>
    );
};

LazyAnalyticsFooter.propTypes = {
    type: PropTypes.string,
    feedType: PropTypes.string,
    mediaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    socialProfileId: PropTypes.number,
    network: PropTypes.string
};

export default LazyAnalyticsFooter;
